import { useCallback, useState, useEffect } from "react";

export const KanbanBoard = ({
  columns,
  bgColors,
  cards,
  onCardMove,
  renderCard,
}) => {
  return (
    <div className="flex h-full w-full gap-2 overflow-x-auto pb-1">
      {columns.map((column) => (
        <KanbanColumn
          key={column}
          title={column}
          column={column}
          cards={cards}
          onCardMove={onCardMove}
          renderCard={renderCard}
        />
      ))}
    </div>
  );
};

const KanbanColumn = ({ title, column, cards, onCardMove, renderCard }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    const handleTouchStart = () => setIsTouched(true);
    const handleTouchEnd = () => setIsTouched(false);

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const cardId = e.dataTransfer.getData("cardId");
      const oldStageId = e.dataTransfer.getData("stageId");
      onCardMove(cardId, column, oldStageId);
    },
    [column, onCardMove]
  );

  const filteredCards = cards.filter((c) => c.column === column);
  let totalAmount = filteredCards.reduce((sum, card) => {
    if (card.type === "manual-quotation") {
      return sum + (card.net_Amount || 0);
    } else if (card.type === "window-quotation") {
      return sum + (card.quote_Amount || 0);
    } else if (card.type === "material-quotation") {
      return sum + (card.net_Amount || 0);
    }
    return sum;
  }, 0);

  return (
    <div
      className="shrink-0 flex flex-col w-[320px] transition-all duration-300"
    >
      <div className="flex flex-col gap-1 mb-2 px-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-gray-800 uppercase tracking-wider">
            {title}
          </h3>
          <span className="flex items-center justify-center bg-white text-primary text-xs font-bold px-2.5 py-1 rounded-full shadow-sm border border-gray-100">
            {filteredCards.length}
          </span>
        </div>
        <div className={`text-[13px] font-semibold text-primary opacity-80 flex items-center gap-1 ${totalAmount > 0 ? '' : 'invisible'}`}>
          ₹{totalAmount > 0 ? totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '0'}
        </div>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsTouched(true)}
        onTouchEnd={() => setIsTouched(false)}
        className={`rounded-xl p-3 bg-[#eef2f6] border border-transparent transition-all duration-300 h-[calc(100vh-320px)] min-h-[400px] overflow-y-auto custom-scrollbar
          ${isHovered || isTouched ? "border-primary/20 bg-[#e9eff5]" : ""}`}
      >
        <div className="flex flex-col gap-2">
          {filteredCards.length > 0 ? (
            filteredCards.map((card) => (
              <div key={card.proforma_No || card.quotation_No} className="w-full">
                {renderCard(card)}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-gray-300 rounded-xl bg-white/50 opacity-60">
              <p className="text-gray-400 text-sm font-medium">Drop cards here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
